import { Component, h } from "preact";
import ImageViewer from "./ImageViewer";
import LabelViewerSizeHandle from "./LabelViewerSizeHandle";
import ImageUploadStep from "./ImageUploader";
import ImageCropStep from "./ImageCropper";
import { IOption, IReactConfiguration } from "../../../typings";
import Uploader from "../../uploader";
import OutputController from "../../../shared/controller/output-controller";
import TemplateController from "../../../shared/controller/template-controller";
import EventController from "../../../shared/controller/event-controller";
import UnitSystemController from "../../../shared/controller/unit-system-controller";
import SizeController from "../../../shared/controller/size-controller";
import ColorController from "../../../shared/controller/color-controller";
import MobileDetect from "mobile-detect";

export default class Viewer extends Component<IViewerProps, IViewerState> {
  private timeout?: number;
  private canvas?: HTMLCanvasElement;
  private lastOffsetY?: number;
  private mobileDetect: MobileDetect;
  private observeIfStickyElement: Element;
  private stickyTimeout?: number;

  constructor(props: IViewerProps) {
    super(props);

    this.mobileDetect = new MobileDetect(window.navigator.userAgent);

    this.state = {
      maxWidthPreviewWrapper: this.isDesktop() ? 300 : 100,
      stateUpload: true,
      stateCrop: false,
      stateImage: false,
      scrolling: false,
    };

    this.handleDrag = this.handleDrag.bind(this);
    this.handleDragStart = this.handleDragStart.bind(this);
    this.openImage = this.openImage.bind(this);
    this.openCropImage = this.openCropImage.bind(this);
    this.openUploadImage = this.openUploadImage.bind(this);
    this.observeIfSticky = this.observeIfSticky.bind(this);
  }

  componentDidMount(): void {
    const eventName: string = EventController.getSaveEventName([
      "image",
      this.props.originalPngName,
    ]);
    document.body
      .querySelector(this.props.conf.target)
      .addEventListener(eventName, this.openImage);

    if (
      this.props.templateLoaded &&
      this.props.outputController.getValue([
        "image",
        this.props.originalPngName,
      ])
    ) {
      this.openImage();
    }

    const element: HTMLElement = document.querySelector(
      this.props.conf.stickyUploadPanel,
    );
    this.observeIfSticky(element);
  }

  observeIfSticky(element: HTMLElement): void {
    if (!element || this.observeIfStickyElement === element) {
      return;
    }
    this.observeIfStickyElement = element;

    const intersectionObserver: IntersectionObserver = new IntersectionObserver(
      // tslint:disable-next-line:typedef
      ([e]) => {
        const scrolling: boolean = e.intersectionRatio < 1;

        if (this.state.scrolling === scrolling) {
          this.stickyTimeout = null;
          clearTimeout(this.stickyTimeout);
          return;
        }

        if (null !== this.stickyTimeout) {
          this.stickyTimeout = null;
          clearTimeout(this.stickyTimeout);
        }

        const lastScrollY: number = window.scrollY;
        const setStateTimeout: () => void = (): void => {
          if (
            (scrolling && lastScrollY > window.scrollY) ||
            (!scrolling && lastScrollY < window.scrollY)
          ) {
            this.stickyTimeout = null;
            return;
          }
          if (
            (scrolling && lastScrollY + 48 < window.scrollY) ||
            (!scrolling && lastScrollY - 48 > window.scrollY)
          ) {
            this.setState({ scrolling });
            this.stickyTimeout = null;
            return;
          }
          this.stickyTimeout = setTimeout(setStateTimeout, 250);
        };
        setStateTimeout();
      },
      { threshold: [1] },
    );

    intersectionObserver.observe(this.observeIfStickyElement);
  }

  handleDrag(offsetY: number): void {
    if (this.timeout) {
      this.lastOffsetY = offsetY;
      return;
    }
    let maxWidthPreviewWrapper: number =
      this.state.maxWidthPreviewWrapper + offsetY;

    if (maxWidthPreviewWrapper === 100 || maxWidthPreviewWrapper === 300) {
      return;
    }

    if (
      this.state.maxWidthPreviewWrapper < maxWidthPreviewWrapper &&
      maxWidthPreviewWrapper > 300
    ) {
      maxWidthPreviewWrapper = 300;
    }

    if (
      this.state.maxWidthPreviewWrapper > maxWidthPreviewWrapper &&
      maxWidthPreviewWrapper < 100
    ) {
      maxWidthPreviewWrapper = 100;
    }

    this.setState({
      maxWidthPreviewWrapper,
    });
    this.timeout = setTimeout(() => {
      this.timeout = undefined;

      if (typeof this.lastOffsetY === "undefined") {
        return;
      }

      this.handleDrag(this.lastOffsetY);
    }, 66);
  }

  handleDragStart(): void {
    if (!this.canvas) {
      return;
    }

    this.setState({
      maxWidthPreviewWrapper: this.canvas.height,
    });
  }

  openImage(): void {
    this.setState({
      stateCrop: false,
      stateImage: true,
      stateUpload: false,
    });
  }

  openCropImage(): void {
    this.setState({
      stateCrop: true,
      stateImage: false,
      stateUpload: false,
    });
  }

  openUploadImage(): void {
    this.setState({
      stateCrop: false,
      stateImage: false,
      stateUpload: true,
    });
  }

  private imageIsSupported(): boolean {
    return true;
  }

  render(): JSX.Element {
    return (
      <div>
        <div>
          {this.state.stateUpload === true && (
            <div className="card__content boo">
              <ImageUploadStep
                main={this.props.main}
                onClose={this.openImage}
                conf={this.props.conf}
                outputController={this.props.outputController}
                sizeController={this.props.sizeController}
                unitSystemController={this.props.unitSystemController}
                colorController={this.props.colorController}
                externalController={this.props.main.externalController}
                templateController={this.props.templateController}
                originalName={this.props.originalName}
                originalMetaName={this.props.originalMetaName}
                originalPngName={this.props.originalPngName}
                croppedDataName={this.props.croppedDataName}
                evenController={this.props.eventController}
                option={this.props.option}
              />
            </div>
          )}

          {this.state.stateCrop === true && (
            <ImageCropStep
              main={this.props.main}
              onClose={this.openImage}
              outputController={this.props.outputController}
              sizeController={this.props.sizeController}
              templateController={this.props.templateController}
              colorController={this.props.colorController}
              originalName={this.props.originalName}
              originalMetaName={this.props.originalMetaName}
              originalPngName={this.props.originalPngName}
              croppedDataName={this.props.croppedDataName}
              option={this.props.option}
            />
          )}

          {this.state.stateImage === true && (
            <ImageViewer
              canvasRef={(canvas: HTMLCanvasElement) => (this.canvas = canvas)}
              conf={this.props.conf}
              main={this.props.main}
              templateLoaded={this.props.templateLoaded}
              eventController={this.props.eventController}
              outputController={this.props.outputController}
              sizeController={this.props.sizeController}
              unitSystemController={this.props.unitSystemController}
              templateController={this.props.templateController}
              originalName={this.props.originalName}
              originalMetaName={this.props.originalMetaName}
              originalPngName={this.props.originalPngName}
              croppedDataName={this.props.croppedDataName}
              option={this.props.option}
              maxHeight={this.state.maxWidthPreviewWrapper}
            />
          )}
          <LabelViewerSizeHandle
            onDrag={this.handleDrag}
            onDragStart={this.handleDragStart}
          />
        </div>

        {this.state.stateImage === true && (
          <div className="mb-6 flex">
            {this.canCrop() && this.imageIsSupported() && this.isDesktop() && (
              <button
                className="table p-3 mx-auto font-bold rounded-md bg-blue-light text-blue"
                onClick={this.openCropImage}
              >
                {this.props.option.meta.fields.cropImageButton}
              </button>
            )}
            <button
              className={
                "table p-3 mx-auto font-bold rounded-md bg-blue-light text-blue" +
                (this.state.scrolling ? " hidden sm:inline-block" : "")
              }
              onClick={this.openUploadImage}
            >
              {this.props.option.meta.fields.uploadImageButton}
            </button>
          </div>
        )}
      </div>
    );
  }

  private isDesktop(): boolean {
    return !this.mobileDetect.phone();
  }

  private canCrop(): boolean {
    if (!this.props.option.attributes) {
      return true;
    }

    if (!this.props.option.attributes["hide-crop"]) {
      return true;
    }

    return !this.props.option.attributes["hide-crop"].value;
  }
}

interface IViewerProps {
  conf: IReactConfiguration;
  selector: string;
  main: Uploader;

  templateLoaded: boolean;

  eventController: EventController;
  outputController: OutputController;
  sizeController: SizeController;
  templateController: TemplateController;
  unitSystemController: UnitSystemController;
  colorController: ColorController;

  originalName: string;
  originalMetaName: string;
  originalPngName: string;
  croppedDataName: string;

  option: IOption;
}

interface IViewerState {
  maxWidthPreviewWrapper: number;
  stateUpload: boolean;
  stateCrop: boolean;
  stateImage: boolean;
  scrolling: boolean;
}
